import { dayjs } from '../support/helpers/dayjs.ts';
import { State } from '@meraki-internal/state';
import { IPaymentTerm, RevenueCatModel } from './revenue-cat/RevenueCatModel.ts';
import { OperatorState } from '../profile/OperatorState.ts';
import { RelativeURL } from '../support/RelativeURL.ts';
import { BufferedEventBus } from '../events/BufferedEventBus.ts';
import { DevSettings } from '../support/DevSettings.ts';
import { Clock } from '../support/Clock.ts';
import { Color } from '../components/common/index.ts';

// year end 2025
const PROMOTION_CONFIG = {
    title: 'Year End Offer',
    start: '2025-12-17T07:00:00.000Z',
    end: '2026-01-02T07:00:00.000Z',
    savingPct: 40,
    image: 'year-end-offer.svg',
    background: 'linear-gradient(168.3deg, #5394BA 0.8%, #84BCA7 32.57%, #B2DD9F 96.11%)',
    badgeBackground: 'linear-gradient(90deg, #5768D1 0%, #2D356B 100%)',
    badgeTextColor: 'light' as Color
};

// black friday 2025
// const PROMOTION_CONFIG = {
//     title: 'Black Friday',
//     start: '2025-11-24T07:00:00.000Z',
//     end: '2025-12-02T07:00:00.000Z',
//     savingPct: 50,
//     image: 'black-friday-offer.svg',
//     background: 'linear-gradient(168deg, #5768D1 0.4%, #4760BC 10.12%, #3958A7 19.16%, #3B59AA 21.98%, #2E5092 32.77%, #25477E 45.91%, #203E69 59.52%, #1C3555 77.6%, #1A2C42 97.99%)',
//     badgeBackground: '#76D764',
//     badgeTextColor: 'dark' as Color
// };

export type ITriggerPaywallSource =
    'new-user' |
    'menu button' |
    'weather summary' |
    'subscription expired' |
    'nudge' |
    'windy' |
    'bulk-export' |
    'mission-export' |
    'log entry' |
    'equipment card' |
    'equipment detail' |
    'equipment default';

export interface IPaywallDialogState {
    isOpen: boolean;
    isWorking: boolean;
    paymentTerm: IPaymentTerm;
}

export class PaywallDialogViewModel extends State<IPaywallDialogState> {

    private _returnTo: string | undefined;
    private _triggerSource: ITriggerPaywallSource | undefined;

    static inject = () => [
        RevenueCatModel,
        OperatorState,
        BufferedEventBus,
        DevSettings,
        Clock
    ];
    constructor(
        private revenueCat: RevenueCatModel,
        private operatorState: OperatorState,
        private events: BufferedEventBus,
        private devSettings: DevSettings,
        private clock: Clock
    ) {
        super({ isOpen: false, isWorking: false, paymentTerm: 'annual' });

        // trigger state emit at promotion start and end so dialog re-renders
        for (const when of [PROMOTION_CONFIG.start, PROMOTION_CONFIG.end]) {
            const msUntil = dayjs(when).diff(dayjs(this.clock.now()));
            if (msUntil > 0) {
                setTimeout(() => this.setState({}), msUntil + 1);
            }
        }
    }

    isNewUser = () => {
        return (this.devSettings.forceNewUserPaywall || this.getTriggerSource() === 'new-user');
    };

    isPromotionActive = () => {
        return (this.devSettings.forcePaywallPromotion || dayjs(this.clock.now()).isBetween(PROMOTION_CONFIG.start, PROMOTION_CONFIG.end));
    };

    getPromotionConfig = () => PROMOTION_CONFIG;

    getReturnToUrl = ({ term }: { term: IPaymentTerm }) => {
        const url = new RelativeURL(this._returnTo); // defaults to current page
        url.searchParams.set('paywallTrigger', this._triggerSource || '');
        url.searchParams.set('paywallTerm', term);
        return url.toString();
    };

    open = ({ triggerSource, returnTo }: { triggerSource: ITriggerPaywallSource, returnTo?: string  }) => {
        if (this.state.isOpen) {
            return;
        }

        this.events.emit('PaywallOpened');

        this._returnTo = returnTo;
        this._triggerSource = triggerSource;

        this.setState({ isOpen: true });
    };

    getTriggerSource = () => this._triggerSource;

    resume = async ({ paymentTerm }: { paymentTerm: IPaymentTerm }): Promise<boolean> => {
        return new Promise(async resolve => {
            try {
                this.setState({ isWorking: true, isOpen: true, paymentTerm });

                const unsubscribe = this.subscribe(() => {
                    if (!this.state.isOpen) {
                        unsubscribe();
                        resolve(this.revenueCat.hasUpgraded());
                    }
                });

                // if login was successful and we are no longer a guest,
                // auto-launch the os-level store purchase dialog
                if (!this.operatorState.isGuest() && this.revenueCat.canUpgrade()) {
                    await this.revenueCat.upgrade(paymentTerm);
                }

            } catch (e) {
                // assume it's clear to the user what went wrong
                // from the messaging in the purchase dialog
            } finally {
                this.setState({ isWorking: false });
            }
        });
    };

    close = () => {
        this._returnTo = undefined;
        this._triggerSource = undefined;
        this.setState({ isOpen: false });
    };

    requirePurchase = async ({ triggerSource, returnTo }: { triggerSource: ITriggerPaywallSource, returnTo?: string  }): Promise<boolean> => {
        if (this.revenueCat.hasUpgraded()) {
            return true;
        }

        return new Promise((resolve, reject) => {
            try {
                this.open({ triggerSource, returnTo });

                // check in case paywall dialog didn't open
                if (!this.state.isOpen) {
                    resolve(false);
                    return;
                }

                const unsubscribe = this.subscribe(() => {
                    if (!this.state.isOpen) {
                        unsubscribe();
                        resolve(this.revenueCat.hasUpgraded());
                    }
                });
            } catch (e) {
                reject(e);
            }
        });
    };
}
